import {VIEW_IDS} from '@github-ui/issue-url-helper/constants/view-constants'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {handleRepositoryDataError} from '@github-ui/relay-error-helpers'
import {MilestoneEdit} from '@github-ui/repository-milestone/MilestoneEdit'
import {useEffect} from 'react'
import {type EntryPointComponent, graphql, type PreloadedQuery, useFragment, usePreloadedQuery} from 'react-relay'

import {RepoIssuesPageLayout} from '../../components/sidebar/RepoIssuesPageLayout'
import {useQueryContext} from '../../contexts/QueryContext'
import {useEntryPointsLoader} from '../../hooks/use-entrypoint-loaders'
import {IssueIndexPage, RepositoryMilestonePage} from '../../lazy-routes'
import {preloadWhenIdle} from '../../utils/preload-when-idle'
import {AnalyticsWrapper} from '../AnalyticsWrapper'
import type {RepositoryMilestoneEditPageContentInternal$key} from './__generated__/RepositoryMilestoneEditPageContentInternal.graphql'
import type {RepositoryMilestoneEditPageQuery} from './__generated__/RepositoryMilestoneEditPageQuery.graphql'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const PageQuery = graphql`
  query RepositoryMilestoneEditPageQuery($name: String!, $owner: String!, $number: Int!) {
    repository(owner: $owner, name: $name) @catch(to: RESULT) {
      id @required(action: THROW)
      ...RepositoryMilestoneEditPageContentInternal @arguments(number: $number)
    }
  }
`

export const RepositoryMilestoneEditPage: EntryPointComponent<
  {pageQuery: RepositoryMilestoneEditPageQuery},
  Record<string, never>
> = ({queries: {pageQuery}}) => {
  const {queryRef} = useEntryPointsLoader(pageQuery, PageQuery)
  if (!queryRef) return null

  return (
    <AnalyticsWrapper category="Repository Milestone Edit">
      <RepositoryMilestoneEditPageContent pageQueryRef={queryRef} />
    </AnalyticsWrapper>
  )
}

function RepositoryMilestoneEditPageContent({
  pageQueryRef,
}: {
  pageQueryRef: PreloadedQuery<RepositoryMilestoneEditPageQuery>
}) {
  const pageData = usePreloadedQuery<RepositoryMilestoneEditPageQuery>(PageQuery, pageQueryRef)
  // eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags -- issue_pinned_views uses actor-aware server-side evaluation via PinnedViewsFeature; isFeatureEnabled only knows current_user.
  const pinnedViewsEnabled = useFeatureFlag('issue_pinned_views')

  useEffect(() => {
    return preloadWhenIdle(() => {
      RepositoryMilestonePage.preload()
      // When issue_pinned_views is on, the sidebar hook preloads IssueIndexPage.
      // Remove this line when the flag is cleaned up.
      if (!pinnedViewsEnabled) {
        IssueIndexPage.preload()
      }
    })
    // pinnedViewsEnabled is read once at mount; flag flips during a session are
    // not expected to retroactively change the preload set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const {setCurrentViewId} = useQueryContext()
  useEffect(() => {
    setCurrentViewId(VIEW_IDS.repository)
  }, [pageQueryRef, setCurrentViewId])

  const repositoryData = pageData?.repository

  const okResult = handleRepositoryDataError(repositoryData)
  if (!okResult || !okResult.value) {
    return null
  }

  return <RepositoryMilestoneEditPageContentInternal repository={okResult.value} />
}

function RepositoryMilestoneEditPageContentInternal({
  repository,
}: {
  repository: RepositoryMilestoneEditPageContentInternal$key
}) {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment RepositoryMilestoneEditPageContentInternal on Repository @argumentDefinitions(number: {type: "Int!"}) {
        ...MilestoneEditFormRepositoryQuery @arguments(number: $number)
      }
    `,
    repository,
  )

  return (
    <RepoIssuesPageLayout dialogTitle="Milestones">
      <MilestoneEdit repository={data} />
    </RepoIssuesPageLayout>
  )
}
